<template>
  <div class="mixed_chart">
    <template v-if="chartData && chartData.topData.count">
      <app-chart chartType="pie" v-if="pieChartOptions" class="pie_chart_container"
        :width="300"
        :height="170"
        :chartOptions="pieChartOptions">
      </app-chart>
      <div class="top_list_box">
        <ul v-if="topItems" class="top_list">
          <li v-for="(item, index) of topItems" :key="index">
            <span class="item rank">
              <span class="marker" :style="{ backgroundColor: pieChartOptions.color[index] }"></span>
              {{ index + 1 | rank }} :
            </span>
            <span class="item label">{{ getChartLabel(item[0]) }}</span>
            <span class="item percentage">{{ item[1] | fixed(1) }}%</span>
          </li>
        </ul>
      </div>
      <app-chart chartType="line" v-if="dailyChartOptions"
        :style="{ position: 'relative', top: '-20px' }"
        :chartOptions="dailyChartOptions"
        :height="180">
      </app-chart>
    </template>
    <analytics-no-data v-else
      :height="350"/>
  </div>
</template>

<script>
import { parse, format, addDays } from 'date-fns'
import { getPercentage } from '@/libs/utils'
import { getTooltipHtml } from '@/libs/chart-helper'
import { getDailyData } from '@/libs/analytics-helper'
import AppChart from '@/components/chart'
import AnalyticsNoData from '@/components/analytics/AnalyticsNoData'

export default {
  name: 'MixedChartAnalytics',
  components: {
    AppChart,
    AnalyticsNoData
  },
  props: [
    'type',
    'name',
    'dailyData',
    'topSize',
    'addEtc',
    'pathData',
    'perVersions',
    'rawData'
  ],
  computed: {
    chartData () {
      const paths = Array.from(this.pathData.paths)
      const countPaths = this.pathData.countPaths

      if (this.perVersions) {
        paths.push('{perVersions}')
      }
      const data = getDailyData(this.dailyData, paths, countPaths, 4, this.addEtc)
      return data
    },
    topItems () {
      if (this.type === 'device') {
        const data = []
        const chartData = this.getDevicePieChartData()

        for (const item of chartData) {
          data.push([item.name, item.value])
        }

        const getDeviceOrder = (device) => {
          switch (device) {
            case 'desktop':
              return 3
            case 'tablet':
              return 2
            case 'phone':
              return 1
          }
        }

        data.sort((a, b) => {
          let aOrder = a[1]
          let bOrder = b[1]

          if (aOrder === bOrder) {
            aOrder = getDeviceOrder(a[0])
            bOrder = getDeviceOrder(b[0])
          }
          return bOrder - aOrder
        })
        return data
      } else {
        return this.chartData.topData.topItems.map(item => {
          return [item[0], item[2]]
        })
      }
    },
    pieChartOptions () {
      let data

      if (this.type === 'device') {
        data = this.getDevicePieChartData()
      } else {
        data = []
        const topData = this.chartData.topData

        for (const item of topData.topItems) {
          data.push({
            name: item[0],
            value: item[2]
          })
        }

        if (topData.etcPercent) {
          data.push({
            name: 'etc',
            value: topData.etcPercent
          })
        }
      }

      const getChartLabel = this.getChartLabel

      return {
        series: [
          {
            type: 'pie',
            name: this.name,
            data,
            label: {
              show: false
            },
            radius: [25, 67],
            center: [100, '50%']
            // emphasis: {
            //   itemStyle: {
            //     shadowColor: '#aaaaaa',
            //     shadowBlur: 7
            //   }
            // }
          }
        ],
        tooltip: {
          formatter (params) {
            const label = getChartLabel(params.data.name)
            const content = [
              [
                `${params.marker}${label} :`,
                `${params.percent.toFixed(1)}%`
              ]
            ]
            return getTooltipHtml(content, params.seriesName)
          }
        },
        color: ['#1199af', '#9fce12', '#ffd738', '#baa7d5', '#e0e0e0']
      }
    },
    dailyChartOptions () {
      const series = []
      const hasSingleData = this.chartData.timestamps.length === 1

      for (let i = 0; i < this.chartData.chartData.length; i++) {
        let chartName

        if (this.chartData.topData.topItems[i]) {
          chartName = this.chartData.topData.topItems[i][0]
        } else {
          chartName = 'etc'
        }

        const defaultData = hasSingleData ? [0] : []
        series.push({
          name: chartName,
          type: 'line',
          stack: 'stack-0',
          data: defaultData.concat(this.chartData.chartData[i]),
          smooth: true,
          lineStyle: {
            width: 1
          },
          areaStyle: {
            opacity: 0.5
          },
          showSymbol: false
        })
      }

      const getChartLabel = this.getChartLabel
      const defaultLabels = []

      if (hasSingleData) {
        const date = parse(this.chartData.timestamps[0])
        const prevDate = addDays(date, -1)
        defaultLabels.push(prevDate.getTime())
      }

      return {
        series,
        xAxis: {
          type: 'category',
          data: defaultLabels.concat(this.chartData.timestamps),
          boundaryGap: false,
          axisLabel: {
            formatter: (item) => format(parse(Number(item)), 'MM-DD')
          }
        },
        yAxis: {
          axisLabel: {
            formatter: '{value}%'
          },
          minInterval: 25,
          maxInterval: 25,
          max: 100
        },
        legend: {
          show: false
        },
        tooltip: {
          formatter (params) {
            const content = []

            for (let i = 0; i < params.length; i++) {
              const param = params[i]
              const label = getChartLabel(param.seriesName)
              content.push([
                `${param.marker}${label} :`,
                `${param.data.toFixed(1)}%`
              ])
            }
            const title = format(parse(Number(params[0].axisValueLabel)), 'MM-DD')
            return getTooltipHtml(content, title)
          }
        },
        color: ['#1199af', '#9fce12', '#ffd738', '#baa7d5', '#e0e0e0']
      }
    }
  },
  methods: {
    getChartLabel (key) {
      let items = key.split('|').filter(item => item !== 'device' && item !== '*')
      items[0] = this.$options.filters.upperFirst(items[0])

      let label

      if (this.perVersions) {
        const version = items[items.length - 1]
        const prefix = items.slice(0, items.length - 1).join(' > ')
        label = `${prefix} ${version}`
      } else {
        label = items.join(' > ')
      }
      return label
    },
    getDevicePieChartData () {
      let data = []
      let total = 0

      for (const deviceData of Object.values(this.rawData.device)) {
        total += deviceData.count
      }

      for (const [device, deviceData] of Object.entries(this.rawData.device)) {
        data.push({
          name: device,
          value: getPercentage(deviceData.count, total)
        })
      }

      return data
    }
  }
}
</script>

<style lang="scss">
.mixed_chart {
  .top_list_box {
    position: absolute;
    overflow: hidden;
    height: 170px;
    left: 234px;
    right: 25px;
    top: 64px;
  }
  .top_list {
    position: absolute;
    top: 50%;
    width: 100%;
    transform: translateY(-50%);
    font-size: 12px;
    color: #444;

    li {
      font-weight: 500;
      position: relative;
      margin: 10px 0 10px 0;
      height: 18px;
      line-height: 18px;
      overflow: hidden;
      background: url(../../assets/img/bg_dotted_line.gif) repeat-x 0 8px;

      &> .item {
        float: left;
        height: 100%;
        background-color: #fff;
      }

      .marker {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 4px;
        margin-top: 2px;
        margin-right: 3px;
      }

      .rank {
        width: 47px;
        font-size: 13px;
      }

      .label {
        display: inline-block;
        max-width: calc(100% - 110px);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-right: 10px;
      }

      .percentage {
        float: right;
        padding-left: 10px;
      }
    }
  }
}
</style>

// https://echarts.apache.org/en/tutorial.html#Overview%20of%20Style%20Customization
